import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import { Button } from 'src/@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import { RowInput } from 'src/components/forms/RowInput';
import { MCP_TOKENS_DESIGN } from './McpTokens.design';

/**
 * Criacao de token MCP em dois passos: `form` (pede so o nome) e `revealed`
 * (mostra o token em texto puro, UMA UNICA VEZ, logo apos o POST). Nao existe
 * hoje em nenhuma outra tela do front esse padrao "revelar uma vez" — depois
 * de fechado, o valor nunca mais e recuperavel (nem pelo backend: so o hash
 * fica salvo).
 */
export default function ModalCriarMcpToken({ open, onClose, onSubmit, laisMcpUrl, clienteId }) {
    const { t } = useTranslation('settings');
    const [step, setStep] = useState('form');
    const [label, setLabel] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [erro, setErro] = useState('');
    const [resultado, setResultado] = useState(null);
    const [copiado, setCopiado] = useState(null); // 'token' | 'comando' | null

    useEffect(() => {
        if (open) {
            setStep('form');
            setLabel('');
            setErro('');
            setResultado(null);
            setCopiado(null);
        }
    }, [open]);

    const handleGerar = async () => {
        if (!label.trim()) {
            setErro(t('apiKeys.mcp.errors.nameRequired', 'Nome é obrigatório'));
            return;
        }
        setSubmitting(true);
        setErro('');
        try {
            const criado = await onSubmit(label.trim());
            setResultado(criado);
            setStep('revealed');
        } catch (err) {
            setErro(err?.mensagem || t('apiKeys.mcp.errors.createFailed', 'Erro ao criar token MCP'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = (texto, qual) => {
        navigator.clipboard.writeText(texto);
        setCopiado(qual);
        setTimeout(() => setCopiado(null), 2000);
    };

    const mcpUrl = `${laisMcpUrl || '<LAIS_MCP_URL>'}/mcp`;
    const comando = resultado
        ? `claude mcp add --transport http lais ${mcpUrl} \\\n  -H "x-internal-token: ${resultado.token}" \\\n  -H "x-lais-client-id: ${clienteId}"`
        : '';

    return (
        <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
            <DialogContent className="sm:max-w-[560px]">
                {step === 'form' ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('apiKeys.mcp.modal.createTitle', 'Novo token MCP')}</DialogTitle>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <RowInput
                                label={t('apiKeys.mcp.modal.nameLabel', 'Nome do token')}
                                input={(
                                    <Input
                                        value={label}
                                        onChange={(e) => setLabel(e.target.value)}
                                        placeholder={t('apiKeys.mcp.modal.namePlaceholder', "Ex: Notebook do João")}
                                        autoFocus
                                    />
                                )}
                            />
                            {erro && <p className="text-sm text-red-600">{erro}</p>}
                        </div>
                        <DialogFooter>
                            <Button onClick={handleGerar} disabled={submitting}>
                                {t('apiKeys.mcp.modal.generateButton', 'Gerar token')}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('apiKeys.mcp.modal.revealedTitle', 'Token gerado')}</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col gap-4 py-2">
                            <div className={MCP_TOKENS_DESIGN.reveal.warningBanner}>
                                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                                <span>{t('apiKeys.mcp.modal.revealWarning', 'Copie agora — por segurança, este token não será mostrado novamente.')}</span>
                            </div>

                            <div className={MCP_TOKENS_DESIGN.reveal.tokenBox}>
                                <span className={MCP_TOKENS_DESIGN.reveal.tokenText}>{resultado?.token}</span>
                                <button
                                    onClick={() => handleCopy(resultado.token, 'token')}
                                    className="p-1.5 hover:bg-white/10 rounded-md text-gray-300 transition-colors flex-shrink-0"
                                    title={t('apiKeys.mcp.modal.copyTokenTooltip', 'Copiar token')}
                                >
                                    {copiado === 'token' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                                </button>
                            </div>

                            <div>
                                <span className={MCP_TOKENS_DESIGN.reveal.commandLabel}>
                                    {t('apiKeys.mcp.modal.commandLabel', 'Comando para conectar no Claude Code')}
                                </span>
                                <div className={MCP_TOKENS_DESIGN.reveal.commandBox}>
                                    <code className={MCP_TOKENS_DESIGN.reveal.commandText}>{comando}</code>
                                    <button
                                        onClick={() => handleCopy(comando, 'comando')}
                                        className={MCP_TOKENS_DESIGN.reveal.copyButton}
                                        title={t('buttons.copy', { ns: 'common', defaultValue: 'Copiar' })}
                                    >
                                        {copiado === 'comando' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                                    </button>
                                </div>
                                {!laisMcpUrl && (
                                    <p className={MCP_TOKENS_DESIGN.reveal.urlMissingNote}>
                                        {t('apiKeys.mcp.modal.commandUrlMissing', 'URL do lais-mcp não configurada neste ambiente — substitua <LAIS_MCP_URL> pelo endereço correto.')}
                                    </p>
                                )}
                            </div>

                            <div>
                                <span className={MCP_TOKENS_DESIGN.reveal.commandLabel}>
                                    {t('apiKeys.mcp.modal.connectorLabel', 'Conector personalizado (claude.ai / Claude Desktop)')}
                                </span>
                                <div className={MCP_TOKENS_DESIGN.reveal.commandBox}>
                                    <code className={MCP_TOKENS_DESIGN.reveal.commandText}>{mcpUrl}</code>
                                    <button
                                        onClick={() => handleCopy(mcpUrl, 'url')}
                                        className={MCP_TOKENS_DESIGN.reveal.copyButton}
                                        title={t('buttons.copy', { ns: 'common', defaultValue: 'Copiar' })}
                                    >
                                        {copiado === 'url' ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                                    </button>
                                </div>
                                <p className={MCP_TOKENS_DESIGN.reveal.connectorHint}>
                                    {t('apiKeys.mcp.modal.connectorHint', 'Em Configurações › Conectores › Adicionar conector personalizado, informe só esta URL. Na tela de autorização que abrir, cole o token acima.')}
                                </p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={onClose}>{t('apiKeys.mcp.modal.doneButton', 'Concluir')}</Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
